/**
 * Date and number formatting. German locale, explicit Europe/Berlin.
 *
 * The timezone is stated, never inherited from the browser: a Buchungstag must not shift
 * because the Kassenwart opened the app from a holiday in another zone
 * (architecture-proposal §3.2, "Timezone").
 */

const TIME_ZONE = 'Europe/Berlin'

const dateFormatter = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeZone: TIME_ZONE,
})

const dateTimeFormatter = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: TIME_ZONE,
})

/** ISO date ("2026-04-13") or Date -> "13. Apr. 2026" */
export function formatDate(value: string | Date): string {
  return dateFormatter.format(toDate(value))
}

/** ISO timestamp -> "13. Apr. 2026, 09:12" */
export function formatDateTime(value: string | Date): string {
  return dateTimeFormatter.format(toDate(value))
}

function toDate(value: string | Date): Date {
  const date = typeof value === 'string' ? new Date(normalisiereZeitstempel(value)) : value
  if (Number.isNaN(date.getTime())) {
    throw new TypeError(`Ungueltiges Datum: ${JSON.stringify(value)}`)
  }
  return date
}

/**
 * Postgres schreibt einen timestamptz als "2025-03-14 19:22:00+00" — Leerzeichen statt T, und
 * ein Offset ohne Minuten. `Date.parse` lehnt beides ab. Die API liefert ISO (node-postgres
 * parst nach Date, JSON serialisiert mit Z); hier wird nur die Postgres-Schreibweise toleriert,
 * damit ein Wert, der aus psql kopiert wurde, nicht eine ganze Seite zu Fall bringt.
 */
function normalisiereZeitstempel(value: string): string {
  return value
    .replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})/, '$1T$2')
    .replace(/(T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)([+-]\d{2})$/, '$1$2:00')
}

/**
 * Whole-day age of an ISO date, counted in Europe/Berlin.
 * Drives the Verrechnungskonto aging list and the GoBD 10-Tage-Kontierungsfrist signal.
 */
export function daysSince(isoDate: string, now: Date = new Date()): number {
  const then = Date.parse(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(then)) {
    throw new TypeError(`Ungueltiges Datum: ${JSON.stringify(isoDate)}`)
  }
  const today = Date.parse(`${berlinIsoDate(now)}T00:00:00Z`)
  return Math.round((today - then) / 86_400_000)
}

/** Today's calendar date in Europe/Berlin, as "YYYY-MM-DD". */
export function berlinIsoDate(now: Date = new Date()): string {
  // 'en-CA' renders ISO-shaped dates; the timeZone option does the actual work.
  return new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE }).format(now)
}
